import { useClose, DialogTitle, Description } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from 'components/Form/Button';
import { Translate } from 'components/Translate/Translate';
import { useTranslation } from 'react-i18next';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { DocumentType, DocumentTypeWithVirtual, ProcessSpecificationDTO, RelationshipV2 } from 'support/types';
import { OCRUpload } from 'components/OCRUpload/OCRUpload';

type UploadStageProps = {
  senderSpec: Pick<ProcessSpecificationDTO, 'fields'>;
  receiverSpec: Pick<ProcessSpecificationDTO, 'fields'>;
  targetRelationship: RelationshipV2;
  documentType: DocumentType;
  virtualDocumentType: DocumentTypeWithVirtual;
  sourceDocumentId: string | undefined;
  initialData?: any;
  onExtracted: (data: any, documentHash: string) => void;
};

export const UploadStage = ({
  senderSpec,
  receiverSpec,
  targetRelationship,
  documentType,
  virtualDocumentType,
  initialData,
  onExtracted,
  sourceDocumentId,
}: UploadStageProps) => {
  const { t } = useTranslation();
  const close = useClose();
  const { data: currentPartner } = useCurrentPartner();

  const senderPartner = targetRelationship.sender_partner;
  const receiverPartner = targetRelationship.receiver_partner;

  return (
    <>
      <div className="mb-4 flex items-start justify-between">
        <div>
          <DialogTitle as="h3" className="text-lg font-medium text-gray-900">
            {t('webedi:documentExtractor.upload.title', {
              documentType: t(`common:messageTypes.singular.${documentType}`),
            })}
          </DialogTitle>
          <Description className="mt-2 text-sm leading-tight text-gray-500">
            <Translate
              i18nKey="webedi:documentExtractor.upload.subTitle"
              values={{ documentType: t(`common:messageTypes.singular.${documentType}`) }}
            />
          </Description>
        </div>
        <Button
          variant="minimal"
          iconOnly
          size="base"
          LeftIcon={XMarkIcon}
          onClick={close}
          analyticsId="document-extractor:close"
        />
      </div>
      <OCRUpload
        relationshipId={targetRelationship.id}
        sourceDocumentId={sourceDocumentId}
        documentType={documentType}
        currentPartner={currentPartner}
        onExtracted={onExtracted}
        senderPartner={senderPartner}
        receiverPartner={receiverPartner}
        senderSpec={senderSpec}
        receiverSpec={receiverSpec}
        initialData={initialData}
        virtualDocumentType={virtualDocumentType}
        trackingIds={{
          uploadStarted: 'webedi:document_upload_started',
          uploadSuccessful: 'webedi:document_upload_successful',
        }}
      />
    </>
  );
};
